<template>
  <div class="generative-block-renderer">
    <component
      :is="componentType"
      v-if="componentType"
      :block="resolvedBlock"
      @action-click="onActionClick"
    />
  </div>
</template>

<script>
import SummaryCardBlock from '@baserow/modules/copilot/components/generative/blocks/SummaryCardBlock'
import SummaryTableBlock from '@baserow/modules/copilot/components/generative/blocks/SummaryTableBlock'
import ChartBlock from '@baserow/modules/copilot/components/generative/blocks/ChartBlock'
import {
  fetchBlockData,
  executeBlockAction,
} from '@baserow/modules/copilot/services/generativeDataService'

/**
 * @typedef {Object} GenerativeBlockAction
 * @property {string} id - Stable identifier for the action.
 * @property {string} label - Human readable label to show in the UI.
 * @property {string} [intent] - High level intent, e.g. 'open_grid', 'approve_row'.
 * @property {string} [icon] - Optional iconoir CSS class.
 * @property {string} [style] - Visual style hint such as 'primary' or 'secondary'.
 * @property {boolean} [disabled] - Whether the action is currently disabled.
 */

/**
 * @typedef {Object} GenerativeBlock
 * @property {string} id - Unique id for the block inside a conversation.
 * @property {string} type - Block type, e.g. 'data_card', 'summary_table', 'mini_chart'.
 * @property {Object} [data] - Block specific payload consumed by the concrete component.
 * @property {Object} [dataSource] - Optional descriptor telling the frontend how to hydrate
 *   the block with live workspace data (workspaceId, applicationId, tableId, etc.).
 * @property {GenerativeBlockAction[]} [actions] - Optional actions that can be triggered
 *   from the block (approve, view in grid, open row, etc.).
 */

export default {
  name: 'GenerativeBlockRenderer',
  components: {
    // Aliases used by this renderer. The underlying components keep their
    // original names but are exposed with semantic keys for the block types.
    DataCard: SummaryCardBlock,
    SummaryTable: SummaryTableBlock,
    MiniChart: ChartBlock,
  },
  props: {
    /**
     * Generic generative UI block payload. The exact schema is defined by
     * the backend contract; this renderer is responsible for delegating
     * to strongly-typed sub-components by `block.type`.
     *
     * @type {GenerativeBlock}
     */
    block: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      resolvedBlock: this.block,
    }
  },
  computed: {
    componentType() {
      const type = this.block && this.block.type
      switch (type) {
        case 'data_card':
        case 'summary_card':
          return 'DataCard'
        case 'summary_table':
          return 'SummaryTable'
        case 'mini_chart':
        case 'chart':
          return 'MiniChart'
        default:
          return null
      }
    },
  },
  watch: {
    block: {
      handler() {
        this.hydrateBlock()
      },
      deep: true,
    },
  },
  created() {
    this.hydrateBlock()
  },
  methods: {
    async hydrateBlock() {
      this.resolvedBlock = await fetchBlockData(this.block, this.$store)
    },
    async onActionClick(action, block) {
      const effectiveBlock = block || this.resolvedBlock
      await executeBlockAction(this, effectiveBlock, action)
    },
  },
}
</script>

<style scoped lang="scss">
.generative-block-renderer {
  margin-bottom: 8px;
}
</style>

